// Landing Page Builds — the deployment supplier's queue (partner role; PMs
// and admins can open it too, for oversight). Backend: the supplier routes
// in app/contentiq/routers/page_builds.py and packages.py — the ONLY
// /contentiq surface a partner can reach, scoped server-side to their own
// supplier. One list of packages (one per campaign), grouped Needs you /
// With DemandArc / Launched by who has the ball; each opens on its own page
// (/contentiq/builds/:packageId — Edward, 2026-10-22: "one place to go… then
// drill into them"): acknowledge with an ETA, "What to build" (the zip,
// brand, each page's frozen spec and corrections) and "What to send back"
// (every link, sent back in one go). Drafts never reach this page.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FONTS, INK, NEUTRAL, HUES, STATE, RADII, card, displayFont, pill, pageShell,
  buttonDisabled, buttonLink, buttonPrimarySmall, buttonSmall,
} from '../../utils/wizardTheme';
import { BUILD_STATUS_LABELS, BUILD_STATUS_PILL, failedChecks } from '../../utils/ciqBuildStatus';
import { sanitizeCopyHtml } from '../../utils/richCopy';
import {
  HELD_STATUSES, IN_REVIEW_STATUSES, buildPageNote, buildPageTitle, builderBucket, byUrgency, isEdmBuild,
  packageZipName, returnLinkFor,
  ballNames, pmSubject, splitFormFields, todayLocalIso,
} from '../../utils/packageStatus';
import { downloadFileBlob } from '../../utils/ciqAssets';
import { useCurrentUser } from '../../context/UserContext';
import CiqBallChips from '../../components/CiqBallChips';
import CiqEdmCopy from '../../components/CiqEdmCopy';
import CiqFollowUpEmails from '../../components/CiqFollowUpEmails';
import CiqPackageEvents from '../../components/CiqPackageEvents';
import { fmtDate } from '../../components/ZbSummary';

const API = process.env.REACT_APP_API_URL;

const SUPPLIER_STATUS_LABELS = {
  ...BUILD_STATUS_LABELS,
  assigned: 'Ready to build',
  url_submitted: 'Submitted — awaiting DemandArc review',
  checked: 'Submitted — awaiting DemandArc review',
  corrections_requested: 'Corrections requested — see notes',
  accepted: '✓ Accepted by DemandArc',
};

const sectionLabel = {
  fontSize: '11.5px', fontWeight: 600, color: INK.secondary, textTransform: 'uppercase',
  letterSpacing: '0.4px', marginBottom: '4px',
};

// "What to build" for one page (or the promotion email): its frozen spec,
// downloads, the latest correction and the check's real findings. Links go
// back through the package's send-back list, not page by page (Edward,
// 2026-10-22).
function BuildSpecCard({ b }) {
  const [err, setErr] = useState('');
  const spec = b.spec || {};
  const isEdm = isEdmBuild(b);
  const [fg, bg] = BUILD_STATUS_PILL[b.status] || [INK.secondary, NEUTRAL.pageBg];
  const pmNotes = (b.notes || []).filter(n => n.side === 'pm');
  const findings = failedChecks(b.check_report);
  const latestCorrection = b.status === 'corrections_requested' && pmNotes.length
    ? pmNotes[pmNotes.length - 1] : null;

  const downloadAsset = async () => {
    setErr('');
    try {
      await downloadFileBlob(`${API}/contentiq/builds/${b.id}/asset`,
        spec.asset?.filename || spec.asset?.title || `asset-${b.id}`);
    } catch { setErr('Could not download the asset file.'); }
  };

  // The client's logo / brand files — served live off the project (Edward,
  // 2026-10-09: "I don't see logo on partner's build page"), so one that
  // arrives after assignment still shows up here.
  const downloadBrand = async (f) => {
    setErr('');
    try {
      await downloadFileBlob(`${API}/contentiq/builds/${b.id}/brand/${f.id}`, f.filename || `brand-${f.id}`);
    } catch { setErr('Could not download the brand file.'); }
  };

  return (
    <div style={{ ...card, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ ...displayFont('16px', 650) }}>{isEdm ? (spec.asset?.title || `Page build ${b.id}`) : buildPageTitle(b)}</span>
        <span style={{ fontSize: '11.5px', color: INK.muted }}>
          {isEdm
            ? 'promotion email — build it and host it; its link goes back with the package'
            : buildPageNote(spec)}
        </span>
        <div style={{ flexGrow: 1 }} />
        <span style={pill(fg, bg)}>{SUPPLIER_STATUS_LABELS[b.status] || b.status}</span>
      </div>

      {latestCorrection && (
        <div style={{ padding: '8px 12px', background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small, fontSize: '12.5px', color: STATE.gate.deep, lineHeight: 1.55 }}>
          <b>Corrections requested:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{latestCorrection.text}</span>
        </div>
      )}

      {/* The auto-check's failed findings — shown to the build team too, so
          a fixable miss (pre-ticked consent box, missing unsubscribe link)
          can be corrected before DemandArc even reviews. */}
      {findings.length > 0 && b.status !== 'accepted' && (
        <div style={{ padding: '8px 12px', background: STATE.danger.tint, border: `1px solid ${STATE.danger.border}`, borderRadius: RADII.small, fontSize: '12px', color: STATE.danger.deep, lineHeight: 1.55 }}>
          <b>The automatic page check found {findings.length} issue{findings.length === 1 ? '' : 's'}:</b>
          <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
            {findings.map(c => (
              <li key={c.id}>{c.label}{c.detail ? ` — ${c.detail}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {spec.asset?.downloadable && (
          <button onClick={downloadAsset} style={buttonSmall}>⬇ Asset file{spec.asset?.filename ? ` (${spec.asset.filename})` : ''}</button>
        )}
        {(b.brand_files || []).map(f => (
          <button key={f.id} onClick={() => downloadBrand(f)} style={buttonSmall}>
            ⬇ Logo / brand ({f.filename})
          </button>
        ))}
      </div>

      {spec.html_title && !isEdm && (
        <div style={{ fontSize: '12.5px', color: INK.primary }}>
          <span style={{ fontSize: '11.5px', fontWeight: 600, color: INK.secondary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Browser tab title:&nbsp;
          </span>
          <b>{spec.html_title}</b>
        </div>
      )}

      {isEdm ? (
        <div>
          <div style={sectionLabel}>
            Email copy — use exactly this; the button links to the first landing page
          </div>
          <CiqEdmCopy edm={spec.edm} />
        </div>
      ) : (spec.flow_pages || []).length > 0 ? (
        <div>
          <div style={sectionLabel}>
            {spec.flow_pages.length > 1
              ? 'This page and the page it leads to — copy exactly as written'
              : 'Page copy — exactly as written'}
          </div>
          <div style={{ fontFamily: FONTS.body, fontSize: '12.5px', color: INK.primary, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px', maxHeight: '340px', overflowY: 'auto', lineHeight: 1.5 }}>
            {spec.flow_pages.map((fp, fi) => (
              <div key={fi} style={{ marginTop: fi === 0 ? 0 : '10px' }}>
                <div style={{ fontWeight: 700 }}>{fp.page_label}{fp.name ? `: ${fp.name}` : ''}</div>
                {(fp.blocks || []).map((blk, bi) => (
                  <div key={bi} style={{ margin: '4px 0 4px 8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '11.5px', color: INK.secondary }}>{blk.label}</span>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{blk.text}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {(spec.follow_up_emails || []).length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <CiqFollowUpEmails emails={spec.follow_up_emails} />
            </div>
          )}
        </div>
      ) : spec.copy_of_record && (
        <div>
          <div style={sectionLabel}>
            Landing page copy — use exactly this{spec.copy_of_record_html ? ' (formatting shows the headings)' : ''}
          </div>
          {spec.copy_of_record_html ? (
            <div style={{ fontFamily: FONTS.body, fontSize: '12.5px', color: INK.primary, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px', maxHeight: '260px', overflowY: 'auto', lineHeight: 1.5 }}
            dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(spec.copy_of_record_html) }} />
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: FONTS.body, fontSize: '12.5px', color: INK.primary, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px', margin: 0, maxHeight: '260px', overflowY: 'auto' }}>
              {spec.copy_of_record}
            </pre>
          )}
        </div>
      )}

      {spec.has_form !== false && (spec.questions?.bant_questions || spec.questions?.custom_questions || spec.questions?.standard_firmographics || (spec.form_fields || []).length > 0) && (
        <div>
          <div style={sectionLabel}>
            Form &amp; questions
          </div>
          <div style={{ fontSize: '12.5px', color: INK.primary, lineHeight: 1.6, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px' }}>
            {(spec.form_fields || []).length > 0 ? (
              [
                ['Fields the visitor fills in', splitFormFields(spec.form_fields).visible],
                ['Hidden fields the page passes along', splitFormFields(spec.form_fields).hidden],
              ].filter(([, list]) => list.length > 0).map(([heading, list], li) => (
                <div key={heading} style={{ marginTop: li ? '6px' : 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '11.5px', color: INK.secondary }}>{heading}</div>
                  {list.map((f, fi) => (
                    <div key={fi} style={{ marginLeft: '8px' }}>
                      {f.client_alias || f.label}
                      {f.tier === 'optional' && <span style={{ color: INK.muted }}> (optional)</span>}
                    </div>
                  ))}
                </div>
              ))
            ) : spec.questions?.standard_firmographics && (
              <div style={{ color: INK.secondary }}>Standard firmographic fields (name, title, company, industry, etc.)</div>
            )}
            {spec.questions?.bant_questions && (spec.questions?.bant_questions_html ? (
              <div style={{ marginTop: '4px', lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(spec.questions.bant_questions_html) }} />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}>{spec.questions.bant_questions}</div>
            ))}
            {spec.questions?.custom_questions && (spec.questions?.custom_questions_html ? (
              <div style={{ marginTop: '4px', lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(spec.questions.custom_questions_html) }} />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}>{spec.questions.custom_questions}</div>
            ))}
          </div>
        </div>
      )}

      {spec.has_form === false && (
        <div style={{ fontSize: '12px', color: INK.secondary }}>
          No registration form on this page — each answer leads on to its path&rsquo;s registration page.
        </div>
      )}

      {spec.has_form !== false && (spec.compliance?.optin_language || (spec.compliance?.privacy_links || []).length > 0) && (
        <div>
          <div style={sectionLabel}>
            Opt-in &amp; privacy — required on the form, exactly as written
          </div>
          {spec.compliance?.optin_proposed && (
            <div style={{ fontSize: '12px', color: STATE.gate.text, marginBottom: '4px' }}>
              Proposed wording — DemandArc is confirming it with the client.
            </div>
          )}
          <div style={{ fontSize: '12.5px', color: INK.primary, lineHeight: 1.6, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px' }}>
            {spec.compliance?.optin_language && (spec.compliance?.optin_language_html ? (
              <div style={{ lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(spec.compliance.optin_language_html) }} />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap' }}>{spec.compliance.optin_language}</div>
            ))}
            {(spec.compliance?.privacy_links || []).map((l, i) => (
              <div key={i} style={{ marginTop: i === 0 && spec.compliance?.optin_language ? '6px' : '2px' }}>
                {l.label ? `${l.label}: ` : ''}
                <a href={l.url} target="_blank" rel="noreferrer" style={{ color: HUES.strategy.text, wordBreak: 'break-all' }}>{l.url}</a>
              </div>
            ))}
          </div>
        </div>
      )}

      {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}





// The client's brand as the build team needs it — read live off the client,
// so colors or a logo DemandArc adds after the send show up here too.
function BrandPanel({ brand }) {
  const b = brand || {};
  const colors = Object.entries(b.colors || {});
  const hasAny = colors.length || (b.logos || []).length || b.legal_language || b.brand_notes
    || (b.restricted_terms || []).length;
  if (!hasAny) {
    return (
      <div style={{ fontSize: '12px', color: INK.secondary }}>
        No brand details yet — DemandArc will send them as part of an update.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px', color: INK.primary,
      background: NEUTRAL.band, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px' }}>
      {colors.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          {colors.map(([token, hex]) => (
            <span key={token} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <span aria-hidden="true" style={{ width: '14px', height: '14px', borderRadius: '4px', background: hex, border: `1px solid ${NEUTRAL.control}` }} />
              <span>{token}</span>
              <code style={{ fontSize: '11.5px', color: INK.secondary }}>{hex}</code>
            </span>
          ))}
        </div>
      )}
      {(b.logos || []).length > 0 && (
        <div><b>Logo{b.logos.length === 1 ? '' : 's'}:</b> {b.logos.join(', ')} <span style={{ color: INK.secondary }}>(in the package's images folder)</span></div>
      )}
      {(b.restricted_terms || []).length > 0 && (
        <div><b>Never use:</b> {b.restricted_terms.join(', ')}</div>
      )}
      {b.legal_language && (
        <div><b>Legal line:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{b.legal_language}</span></div>
      )}
      {b.brand_notes && (
        <div><b>Brand notes:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{b.brand_notes}</span></div>
      )}
      {b.publisher && (
        <div style={{ color: INK.secondary }}>Publisher on every page: {b.publisher}</div>
      )}
    </div>
  );
}

const buildTitle = (b) => (isEdmBuild(b) ? (b.spec?.asset?.title || 'Promotion email') : buildPageTitle(b));

// "What to send back" (Edward, 2026-10-22: "I don't like how you have to
// submit individual URLs"). One list: a link slot for every page the build
// team holds, saved as they go, then ONE send that returns the whole package
// — one notification, the ball moves as a whole. Pages DemandArc is already
// reviewing show their link (fixable in place); accepted ones are done.
// readOnly: a PM looking over the build team's shoulder — links show,
// nothing is editable or sendable (usability pass, 2026-10-22). Admins may
// act for the build team (Edward, 2026-10-23), so they get the controls.
// onDirtyChange lets the page warn before unsaved links are lost.
function SendBackPanel({ pkg, onReload, launched, readOnly, onDirtyChange }) {
  const builds = pkg.builds || [];
  const held = builds.filter(b => HELD_STATUSES.includes(b.status));
  const inReview = builds.filter(b => IN_REVIEW_STATUSES.includes(b.status));
  const accepted = builds.filter(b => b.status === 'accepted');
  const [links, setLinks] = useState(() => Object.fromEntries(held.map(b => [b.id, returnLinkFor(pkg, b)])));
  const [dirty, setDirtyState] = useState(false);
  const setDirty = (v) => { setDirtyState(v); if (onDirtyChange) onDirtyChange(v); };
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);
  const [fixing, setFixing] = useState(null);   // {id, url}

  const ready = held.filter(b => (links[b.id] || '').trim()).length;
  const allReady = held.length > 0 && ready === held.length;

  const setLink = (id, v) => { setLinks(l => ({ ...l, [id]: v })); setDirty(true); setSaved(false); };

  const saveLinks = async () => {
    const body = { links: Object.fromEntries(held.map(b => [String(b.id), (links[b.id] || '').trim()])) };
    await axios.put(`${API}/contentiq/packages/${pkg.id}/return-links`, body);
    setDirty(false);
  };

  const save = async () => {
    setBusy(true); setErr('');
    try { await saveLinks(); setSaved(true); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not save the links.'); }
    finally { setBusy(false); }
  };

  const sendBack = async () => {
    if (!allReady) { setErr('Add a link for every page first.'); return; }
    setBusy(true); setErr('');
    try {
      await saveLinks();
      await axios.post(`${API}/contentiq/packages/${pkg.id}/send-back`, { note: note.trim() || null });
      setNote('');
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not send the package back.'); }
    finally { setBusy(false); }
  };

  const saveFix = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/builds/${fixing.id}/url`, { url: fixing.url.trim() });
      setFixing(null);
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not update the link.'); }
    finally { setBusy(false); }
  };

  const inputStyle = { flex: '1 1 260px', minWidth: 0, padding: '7px 10px', fontSize: '12.5px', fontFamily: FONTS.body,
    border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small, background: NEUTRAL.pageBg };
  const rowStyle = { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '8px 0',
    borderTop: `1px solid ${NEUTRAL.borderSoft}` };
  const nameStyle = { flex: '0 1 260px', minWidth: 0, fontSize: '13px', fontWeight: 600, color: INK.primary };

  return (
    <div id="ciq-send-back" style={{ ...card, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <span style={{ ...displayFont('16px', 700) }}>What to send back</span>
        {held.length > 0 && (
          <span style={{ fontSize: '12px', color: allReady ? STATE.done.text : INK.secondary }}>
            {ready} of {held.length} link{held.length === 1 ? '' : 's'} ready
          </span>
        )}
      </div>
      {held.length === 0 && (
        <div style={{ fontSize: '12.5px', color: INK.secondary, paddingBottom: '4px' }}>
          {launched ? 'Launched — nothing to send.' : 'Nothing to send right now — every link is with DemandArc.'}
        </div>
      )}
      {held.map(b => (
        <div key={b.id} style={rowStyle}>
          <span style={nameStyle}>
            {buildTitle(b)}
            <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 400, color: b.status === 'corrections_requested' ? STATE.gate.text : INK.secondary }}>
              {isEdmBuild(b) ? 'hosted email link' : 'landing page URL'}
              {b.spec?.page_role === 'entry' ? ' · DemandArc starts the review here' : ''}
              {b.status === 'corrections_requested' ? ' · corrections requested' : ''}
            </span>
          </span>
          {readOnly ? (
            <span style={{ flex: '1 1 260px', minWidth: 0, fontSize: '12px', color: links[b.id] ? INK.body : INK.muted, wordBreak: 'break-all' }}>
              {links[b.id] || 'no link yet'}
            </span>
          ) : (
            <input value={links[b.id] || ''} onChange={e => setLink(b.id, e.target.value)} placeholder="https://…"
              aria-label={`Link for ${buildTitle(b)}`} style={inputStyle} />
          )}
        </div>
      ))}
      {inReview.map(b => (
        <div key={b.id} style={rowStyle}>
          <span style={nameStyle}>
            {buildTitle(b)}
            <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 400, color: HUES.strategy.text }}>sent — DemandArc is reviewing</span>
          </span>
          {fixing?.id === b.id ? (
            <span style={{ display: 'flex', gap: '6px', flex: '1 1 260px', flexWrap: 'wrap' }}>
              <input value={fixing.url} onChange={e => setFixing({ ...fixing, url: e.target.value })} style={inputStyle} />
              <button onClick={saveFix} disabled={busy || !fixing.url.trim()} style={{ ...buttonPrimarySmall(), ...(busy || !fixing.url.trim() ? buttonDisabled : {}) }}>Save link</button>
              <button onClick={() => setFixing(null)} style={buttonSmall}>Cancel</button>
            </span>
          ) : (
            <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: '1 1 260px', minWidth: 0, flexWrap: 'wrap' }}>
              <a href={b.url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: HUES.strategy.text, wordBreak: 'break-all' }}>{b.url}</a>
              {!launched && !readOnly && (
                <button onClick={() => setFixing({ id: b.id, url: b.url || '' })} style={{ ...buttonLink, fontSize: '12px' }}>
                  ✎ Edit link
                </button>
              )}
            </span>
          )}
        </div>
      ))}
      {accepted.map(b => (
        <div key={b.id} style={rowStyle}>
          <span style={nameStyle}>{buildTitle(b)}</span>
          <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: '1 1 260px', minWidth: 0, flexWrap: 'wrap' }}>
            <a href={b.url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: HUES.strategy.text, wordBreak: 'break-all' }}>{b.url}</a>
            <span style={pill(STATE.done.text, STATE.done.tint)}>{b.launched ? '🚀 Launched' : '✓ Accepted'}</span>
          </span>
        </div>
      ))}
      {held.length > 0 && !launched && !readOnly && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: `1px solid ${NEUTRAL.borderSoft}`, paddingTop: '10px' }}>
          <label style={{ fontSize: '12px', color: INK.secondary }}>
            Note to DemandArc (optional)
            <input value={note} onChange={e => setNote(e.target.value)}
              style={{ ...inputStyle, display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '3px' }} />
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={sendBack} disabled={busy || !allReady}
              title={allReady ? '' : 'Add a link for every page first'}
              style={{ ...buttonPrimarySmall(), ...(busy || !allReady ? buttonDisabled : {}) }}>
              Send the package back
            </button>
            <button onClick={save} disabled={busy || !dirty} style={{ ...buttonSmall, ...(busy || !dirty ? buttonDisabled : {}) }}>Save links</button>
            {saved && !dirty && <span style={{ fontSize: '12px', color: STATE.done.text }}>✓ Saved</span>}
            {dirty && <span style={{ fontSize: '12px', color: STATE.gate.text }}>Unsaved links</span>}
          </div>
          <div style={{ fontSize: '11.5px', color: INK.secondary }}>
            Save as you go; the package goes back to DemandArc in one send once every link is in.
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}

function PackageHeader({ pkg, names }) {
  const builds = pkg.builds || [];
  const pageCount = builds.filter(b => !isEdmBuild(b)).length;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 320px', minWidth: 0 }}>
        <span style={{ ...displayFont('20px', 700) }}>{pkg.project_name}</span>
        <span style={{ fontSize: '12px', color: INK.secondary }}>
          {[pkg.brand?.client_name, pkg.format_name, `v${pkg.version}`,
            `${pageCount} page${pageCount === 1 ? '' : 's'}${pkg.include_edm ? ' + email' : ''}`]
            .filter(Boolean).join(' · ')}
          {pkg.sent_at ? ` · sent ${fmtDate(pkg.sent_at)}` : ''}
          {pkg.sent_by_name ? ` by ${pkg.sent_by_name}` : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
        <span style={pill(INK.body, NEUTRAL.chipBg)}>{pkg.status_label}</span>
        <CiqBallChips ball={pkg.ball} perspective="builder" names={names} />
      </div>
    </div>
  );
}

// One package, drilled into from the list: the acknowledge-with-ETA step,
// DemandArc's note and anything to follow, the launch confirmation (one per
// package — design settled 2026-10-08), then "What to build" and "What to
// send back".
const ETA_DONE_STATUSES = ['complete', 'client_review', 'approved', 'launch_requested'];

function PackageDetail({ pkg, onReload, onBack, readOnly, forTeam, currentUserId }) {
  const [etaOpen, setEtaOpen] = useState(false);
  const [linksDirty, setLinksDirty] = useState(false);
  // The ETA starts at today (Edward, 2026-10-28) — the team moves it on.
  const [eta, setEta] = useState(pkg.eta || todayLocalIso());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showAccepted, setShowAccepted] = useState(false);
  const builds = pkg.builds || [];
  const toBuild = builds.filter(b => b.status !== 'accepted');
  const accepted = builds.filter(b => b.status === 'accepted');
  const needsAck = !pkg.acknowledged_at;
  const launched = pkg.status === 'launched';
  const latestRevision = [...(pkg.events || [])].reverse().find(e => e.kind === 'revised');
  const heldCount = builds.filter(b => HELD_STATUSES.includes(b.status)).length;
  // "You" is the reader; the other side is named (Edward, 2026-10-23).
  const names = ballNames(pkg, { isTeam: !forTeam, currentUserId });
  const team = pkg.team_name || 'the build team';
  const teamPossessive = pkg.team_name ? `${pkg.team_name}\u2019s` : 'Build team\u2019s';
  const pmWho = forTeam ? pmSubject(pkg, currentUserId) : (pkg.pm_name || 'DemandArc');
  const yourEta = forTeam ? `${teamPossessive} ETA` : 'Your ETA';

  // Links typed but not saved would vanish on "← All packages" or a tab close.
  useEffect(() => {
    if (!linksDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [linksDirty]);
  const back = () => {
    if (linksDirty && !window.confirm('Leave without saving your links? Links you typed since the last save are lost.')) return;
    onBack();
  };

  const acknowledge = async () => {
    if (!eta) { setErr('Pick the date the review links will be ready.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/packages/${pkg.id}/acknowledge`, { eta });
      setEtaOpen(false);
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not acknowledge the package.'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setErr('');
    try {
      await downloadFileBlob(`${API}/contentiq/packages/${pkg.id}/download`, packageZipName(pkg.project_name, pkg.version));
    } catch { setErr('Could not download the package.'); }
  };

  const markLaunched = async () => {
    if (!builds.length) return;
    const told = pmWho === 'You' ? 'You get the launch notice.' : `${pmWho} is notified.`;
    const what = forTeam ? `Mark this campaign launched for ${team}?` : 'Mark this campaign as launched?';
    if (!window.suppressConfirm && !window.confirm(`${what} ${told}`)) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/builds/${builds[0].id}/launched`);
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not mark launched.'); }
    finally { setBusy(false); }
  };

  const etaForm = (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="date" value={eta} onChange={e => setEta(e.target.value)} aria-label="Review links ready by"
        style={{ padding: '7px 10px', fontSize: '13px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small, background: NEUTRAL.pageBg }} />
      <button onClick={acknowledge} disabled={busy} style={{ ...buttonPrimarySmall(), ...(busy ? buttonDisabled : {}) }}>
        {needsAck ? 'Acknowledge package' : 'Update ETA'}
      </button>
      {!needsAck && <button onClick={() => { setEtaOpen(false); setEta(pkg.eta || todayLocalIso()); setErr(''); }} style={buttonSmall}>Cancel</button>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '900px' }}>
      <button onClick={back} style={{ ...buttonLink, fontSize: '13px', alignSelf: 'flex-start' }}>
        ← All packages
      </button>
      <div style={{ ...card, padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <PackageHeader pkg={pkg} names={names} />
        {!launched && pkg.builder_next_step && (
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: INK.primary, display: 'flex', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span>{forTeam ? `${teamPossessive} next step` : 'Next'}: {forTeam ? (pkg.team_next_step || pkg.builder_next_step) : pkg.builder_next_step}</span>
            {heldCount > 0 && (
              <button onClick={() => {
                const el = document.getElementById('ciq-send-back');
                if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }} style={{ ...buttonLink, fontSize: '12px' }}>Jump to What to send back ↓</button>
            )}
          </div>
        )}
        {needsAck && !launched && readOnly && (
          <div style={{ fontSize: '12.5px', color: INK.secondary }}>
            {pkg.team_name || 'The build team'} hasn&rsquo;t acknowledged this package yet.
            {pkg.needed_by ? ` ${pmWho} need${pmWho === 'You' ? '' : 's'} the links by ${fmtDate(pkg.needed_by)}.` : ''}
          </div>
        )}
        {needsAck && !launched && !readOnly && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 14px', background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small }}>
            <div style={{ fontSize: '12.5px', color: STATE.gate.deep, lineHeight: 1.5 }}>
              {forTeam
                ? <><b>Acknowledge this package for {team}</b> and set when the review links will be ready.</>
                : <><b>Acknowledge this package</b> and tell {pmWho} when the review links will be ready.</>}
              {pkg.needed_by ? ` ${pmWho === 'You' ? 'You need' : `${pmWho} needs`} them by ${fmtDate(pkg.needed_by)}.` : ''}
            </div>
            {etaForm}
          </div>
        )}
        {!needsAck && !launched && (
          <div style={{ fontSize: '12.5px', color: INK.body, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {etaOpen && !readOnly ? etaForm : (
              <>
                <span>
                  {pkg.eta ? <>{yourEta} <b>{fmtDate(pkg.eta)}</b></> : 'No ETA set yet'}
                  {pkg.eta_passed && <span style={{ color: STATE.danger.text, fontWeight: 600 }}> — passed</span>}
                  {pkg.needed_by ? ` · ${pmWho} need${pmWho === 'You' ? '' : 's'} the links by ${fmtDate(pkg.needed_by)}` : ''}
                </span>
                {/* Once every page is accepted the ETA has nothing left to promise. */}
                {!readOnly && !ETA_DONE_STATUSES.includes(pkg.status) && (
                  <button onClick={() => setEtaOpen(true)} style={{ ...buttonLink, fontSize: '12px' }}>
                    {pkg.eta ? 'Change ETA' : 'Set ETA'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {pkg.status === 'launch_requested' && (
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', border: `1px solid ${STATE.done.border}`, background: STATE.done.tint, borderRadius: RADII.small }}>
            <span style={{ fontSize: '13px', color: STATE.done.deep, lineHeight: 1.5 }}>
              <b>🚀 Ready to launch.</b> DemandArc confirmed every page is approved: {forTeam
                ? 'the build team takes the campaign live, then marks it launched.'
                : 'take the campaign live, then mark it launched.'}
            </span>
            <div style={{ flexGrow: 1 }} />
            {!readOnly && (
              <button onClick={markLaunched} disabled={busy}
                style={{ ...buttonPrimarySmall(STATE.done.main), ...(busy ? buttonDisabled : {}) }}>Mark launched</button>
            )}
          </div>
        )}
        {latestRevision && (
          <div style={{ fontSize: '12.5px', color: HUES.strategy.deep, background: HUES.strategy.tint, border: `1px solid ${HUES.strategy.border}`, borderRadius: RADII.small, padding: '8px 12px', lineHeight: 1.5 }}>
            <b>Updated —</b> {latestRevision.text}
          </div>
        )}
        {pkg.note && (
          <div style={{ fontSize: '12.5px', color: INK.body, lineHeight: 1.5 }}>
            <b>Note from {pkg.sent_by_name || pkg.pm_name || 'DemandArc'}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{pkg.note}</span>
          </div>
        )}
        {(pkg.to_follow || []).length > 0 && !launched && (
          <div style={{ fontSize: '12px', color: STATE.gate.text, lineHeight: 1.5 }}>
            <b>To follow from {pkg.pm_name || 'DemandArc'}:</b> {pkg.to_follow.join(' · ')} — build around them; they arrive as an update.
          </div>
        )}
        {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
      </div>

      <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ ...displayFont('16px', 700) }}>What to build</span>
          <div style={{ flexGrow: 1 }} />
          <button onClick={download} style={buttonSmall}>⬇ Download package (.zip)</button>
        </div>
        <div style={{ fontSize: '12px', color: INK.secondary, marginTop: '-6px' }}>
          The zip holds the build guide, QA checklist, the settings file for your page template, images and every page's copy.
        </div>
        <div style={{ ...card, padding: '14px 18px' }}>
          <div style={sectionLabel}>Brand</div>
          <BrandPanel brand={pkg.brand} />
        </div>
        {toBuild.map(b => <BuildSpecCard key={b.id} b={b} />)}
        {accepted.length > 0 && (
          <div>
            <button onClick={() => setShowAccepted(v => !v)}
              style={{ ...buttonLink, ...sectionLabel, marginBottom: '6px' }}>
              {showAccepted ? '▾' : '▸'} Accepted pages ({accepted.length})
            </button>
            {showAccepted && accepted.map(b => <BuildSpecCard key={b.id} b={b} />)}
          </div>
        )}
      </section>

      <SendBackPanel key={`${pkg.id}-${pkg.version}-${(pkg.builds || []).map(b => b.status).join()}`}
        pkg={pkg} onReload={onReload} launched={launched} readOnly={readOnly} onDirtyChange={setLinksDirty} />

      {(pkg.events || []).length > 0 && (
        <details style={{ fontSize: '12px', color: INK.secondary }}>
          <summary style={{ cursor: 'pointer', color: HUES.strategy.text }}>Package history</summary>
          <div style={{ marginTop: '6px' }}><CiqPackageEvents events={pkg.events} /></div>
        </details>
      )}
    </div>
  );
}

// One row in the list — enough to know what's waiting and how urgent it is.
function PackageRow({ pkg, onOpen, forTeam, currentUserId }) {
  const held = (pkg.builds || []).filter(b => HELD_STATUSES.includes(b.status));
  const ready = held.filter(b => returnLinkFor(pkg, b).trim()).length;
  return (
    <button onClick={onOpen}
      style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        textAlign: 'left', cursor: 'pointer', fontFamily: FONTS.body, width: '100%' }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 320px', minWidth: 0 }}>
        <span style={{ ...displayFont('15px', 700) }}>{pkg.project_name}</span>
        <span style={{ fontSize: '12px', color: INK.secondary }}>
          {[pkg.brand?.client_name, pkg.format_name, `v${pkg.version}`].filter(Boolean).join(' · ')}
          {pkg.status !== 'launched' && pkg.builder_next_step ? ` — ${forTeam ? (pkg.team_next_step || pkg.builder_next_step) : pkg.builder_next_step}` : ''}
        </span>
        {pkg.status !== 'launched' && (
          <span style={{ fontSize: '11.5px', color: pkg.eta_passed ? STATE.danger.text : INK.muted }}>
            {pkg.eta ? `${forTeam ? `${pkg.team_name ? `${pkg.team_name}\u2019s` : 'Build team\u2019s'} ETA` : 'Your ETA'} ${fmtDate(pkg.eta)}${pkg.eta_passed ? ' — passed' : ''}`
              : (pkg.acknowledged_at ? 'No ETA set' : 'Not acknowledged yet')}
            {pkg.needed_by ? ` · needed by ${fmtDate(pkg.needed_by)}` : ''}
            {held.length > 0 ? ` · ${ready} of ${held.length} links ready` : ''}
          </span>
        )}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
        <span style={pill(INK.body, NEUTRAL.chipBg)}>{pkg.status_label}</span>
        <CiqBallChips ball={pkg.ball} perspective="builder" names={ballNames(pkg, { isTeam: !forTeam, currentUserId })} />
      </span>
      <span aria-hidden="true" style={{ color: HUES.strategy.text, fontSize: '16px' }}>→</span>
    </button>
  );
}

const GROUPS = [
  { key: 'needs_you', label: 'Needs you', pmLabel: 'Needs the build team', empty: 'Nothing waiting on you right now.',
    pmEmpty: 'Nothing waiting on the build team right now.' },
  { key: 'with_demandarc', label: 'With DemandArc', empty: '' },
  { key: 'launched', label: 'Launched', empty: '' },
];

export default function ContentIQBuilds() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [err, setErr] = useState('');
  const [showLaunched, setShowLaunched] = useState(false);
  // The page is the build team's to-do list. PMs open it for oversight,
  // read-only (usability pass, 2026-10-22); admins may act for the build team
  // (Edward, 2026-10-23) — logged as the team's step, under the admin's name.
  // forTeam: the viewer isn't the build team, so labels speak about the team.
  const { currentUser } = useCurrentUser() || {};
  const role = currentUser?.role;
  const readOnly = role !== 'partner' && role !== 'admin';
  const forTeam = role !== 'partner';

  const load = () => axios.get(`${API}/contentiq/packages/assigned`)
    .then(r => { setPackages(r.data.packages || []); setErr(''); })
    .catch(() => setErr('Could not load your build packages.'));
  useEffect(() => { load(); }, []);

  const current = packageId && packages ? packages.find(p => String(p.id) === String(packageId)) : null;

  return (
    <div style={{ ...pageShell, padding: '26px 34px', fontFamily: FONTS.body }}>
      {err && <div style={{ fontSize: '13px', color: STATE.danger.text, marginBottom: '12px' }}>{err}</div>}
      {packages === null && !err && <div style={{ fontSize: '13px', color: INK.muted }}>Loading…</div>}

      {packageId && packages !== null && (current
        ? <PackageDetail pkg={current} onReload={load} onBack={() => navigate('/contentiq/builds')} readOnly={readOnly} forTeam={forTeam} currentUserId={currentUser?.id} />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '13px', color: INK.secondary }}>That package isn't in your queue.</div>
            <button onClick={() => navigate('/contentiq/builds')} style={{ ...buttonLink, fontSize: '13px', alignSelf: 'flex-start' }}>
              ← All packages
            </button>
          </div>
        ))}

      {!packageId && packages !== null && (
        <>
          <div style={{ ...displayFont('24px', 700), letterSpacing: '-0.3px', marginBottom: '4px' }}>
            Landing Page Builds
          </div>
          <div style={{ fontSize: '13px', color: INK.secondary, marginBottom: '18px', maxWidth: '680px', lineHeight: 1.55 }}>
            Each package is one campaign: its landing pages (and the promotion email, when
            included). Open one to see what to build, then send all the links back together.
          </div>
          {readOnly && (
            <div role="status" style={{ maxWidth: '900px', marginBottom: '18px', padding: '10px 14px', fontSize: '12.5px',
              color: HUES.strategy.deep, background: HUES.strategy.tint, border: `1px solid ${HUES.strategy.border}`, borderRadius: RADII.small }}>
              <b>Build team view — read only.</b> This is what the build team sees. You act on a package from its
              project&rsquo;s Page build, or filter All projects to &ldquo;With the build team&rdquo;.
            </div>
          )}
          {!readOnly && forTeam && (
            <div role="status" style={{ maxWidth: '900px', marginBottom: '18px', padding: '10px 14px', fontSize: '12.5px',
              color: STATE.gate.deep, background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small }}>
              <b>Acting for the build team.</b> As an admin you can acknowledge, send links back and mark a campaign
              launched for the build team. Each step is logged as the build team&rsquo;s, with your name on it.
            </div>
          )}
          {packages.length === 0 && (
            <div style={{ fontSize: '13px', color: INK.muted }}>No build packages assigned right now.</div>
          )}
          {packages.length > 0 && GROUPS.map(g => {
            const list = packages.filter(p => builderBucket(p) === g.key).sort(byUrgency);
            if (!list.length && !g.empty) return null;
            const collapsible = g.key === 'launched';
            const shown = !collapsible || showLaunched;
            return (
              <section key={g.key} style={{ maxWidth: '900px', marginBottom: '22px' }}>
                <div style={{ marginBottom: '10px' }}>
                  {collapsible ? (
                    <button onClick={() => setShowLaunched(v => !v)}
                      style={{ ...buttonLink, ...displayFont('16px', 700) }}>
                      {showLaunched ? '▾' : '▸'} {g.label} · {list.length}
                    </button>
                  ) : (
                    <span style={{ ...displayFont('16px', 700) }}>{(forTeam && g.pmLabel) || g.label} · {list.length}</span>
                  )}
                </div>
                {shown && list.length === 0 && <div style={{ fontSize: '13px', color: INK.muted }}>{(forTeam && g.pmEmpty) || g.empty}</div>}
                {shown && list.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {list.map(p => <PackageRow key={p.id} pkg={p} forTeam={forTeam} currentUserId={currentUser?.id} onOpen={() => navigate(`/contentiq/builds/${p.id}`)} />)}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
